// Port of load_capitalone_csv in scripts/loaders.py.
// Separate Debit / Credit columns. All rows share account="CapitalOne" —
// card last4 lives on the separate account_last4 field (per "no account-
// number-based matching" policy).

import {
  type Transaction,
  newTransaction,
  makeId,
  parseDate,
  parseAmount,
  normalizeVendor,
  isInterPortalTransfer,
  extractLast4,
} from "../common.js";
import { parseCsv, toDictRows, findHeaderIndex } from "../csv.js";

export async function loadCapitalOne(
  text: string,
  filename: string,
): Promise<Transaction[]> {
  const rows = parseCsv(text);
  const headerIdx = findHeaderIndex(rows, ["Posted Date", "Description"]);
  if (headerIdx < 0) return [];
  const header = rows[headerIdx].map((h) => (h || "").trim());
  const dictRows = toDictRows(header, rows.slice(headerIdx + 1));

  const out: Transaction[] = [];
  for (const row of dictRows) {
    const rawDate = (row["Posted Date"] || row["Transaction Date"] || "").trim();
    if (!rawDate) continue;
    let dateStr: string;
    try {
      dateStr = parseDate(rawDate);
    } catch {
      continue;
    }
    const debit = parseAmount(row["Debit"] || "0");
    const credit = parseAmount(row["Credit"] || "0");
    let amount: number;
    if (debit) amount = debit;
    else if (credit) amount = -credit;
    else continue;

    const description = (row["Description"] || "").trim().replace(/^"|"$/g, "");
    if (isInterPortalTransfer(description)) continue;

    const accountLabel = "CapitalOne";
    const id = await makeId("capitalone", accountLabel, dateStr, amount, description);
    out.push(
      newTransaction({
        id,
        date: dateStr,
        vendor_raw: description,
        vendor_key: normalizeVendor(description) || "capitalone",
        amount: Math.round(amount * 100) / 100,
        portal: "capitalone",
        account: accountLabel,
        account_kind: "liability", // Capital One only ships credit-card exports
        account_last4: extractLast4(row["Card No."] || row["Card"] || ""),
        source_file: filename,
        raw: { ...row },
      }),
    );
  }
  return out;
}
